<template>
  <div class="component-box">

    <div class="article-sum-box">

      <div class="article-flex-box">

        <div class="cover-img-container" v-if="identified && article.coverImgId">
          <div class="cover-img-box" >
            <c-img :articleImg="true" childclass="article-sum-img" :imgId="article.coverImgId"/>
          </div>
        </div>
      
        <div class="content-box">
          <div class="article-title">{{ article.title }}</div>

          <div v-if="$mq !== 'mobile' && $mq !== 'tablet'">
            <div class="text-content">{{ article.summary }}</div>

            <div class="read-article" v-on:click="readArticle()">
              <div class="read-article-label">Lire l'article</div>
              <v-icon color="primary">mdi-arrow-right-drop-circle-outline</v-icon>
            </div>

            <div class="separator"></div>

            <div class="article-details">
              <div class="creation-details">{{ getArticleDetails(article.authorName, article.createdAt) }}</div>
              <div class="rubrics-list"><article-rubric-list :rubrics="article.rubrics"/></div>
            </div>

          </div>
        </div>
      </div>

      <div class="content-box" v-if="$mq === 'mobile' || $mq === 'tablet'">
        <div class="text-content">{{ article.summary }}</div>

        <div class="read-article" v-on:click="readArticle()">
          <div class="read-article-label">Lire l'article</div>
          <v-icon color="primary">mdi-arrow-right-drop-circle-outline</v-icon>
        </div>

        <div class="separator"></div>

        <div class="article-details mobile">
          <div class="creation-details">{{ getArticleDetails(article.authorName, article.createdAt) }}</div>
        </div>  
        <div class="rubrics-list"><article-rubric-list :rubrics="article.rubrics"/></div>       
      </div>

    </div>
  </div>
</template>

<style scoped>
  @import '../../assets/styles/global-styles.css';
  @import '../../assets/styles/variables.css';

  .component-box {
    padding: calc(6px * var(--margin-factor));
  }

  .article-sum-box {
    width: 100%;
    overflow: auto;
    background-color: var(--article-sum-background);
    padding: calc(15px * var(--margin-factor));
    border-radius: var(--page-border-radius);
  }

  @media only screen and (max-width: 900px) {
    .cover-img-box {
      width: 70px;
      height: 70px;
    }
  }

  @media only screen and (min-width: 900px) {
    .cover-img-box {
      width: 130px;
      height: 130px;
    }
  }

  .article-flex-box {
    display: flex;
  }

  .cover-img-container {
    padding:  calc(8px * var(--margin-factor));
  }

  .cover-img-box {
    background: var(--empty-img-background);
    border-radius: 45px;

    box-shadow: inset 0px -2px 8px #7850c2b2;
    border-style: solid;
    border-color: aliceblue;
  }


  .content-box {
    padding: 5px calc(25px * var(--margin-factor));
    width: 100%;
  }

  .article-title {
    font-size: 1.4em;
    color: var(--dark-text-color);
    font-weight: bold;
    margin-bottom: 7px;
  }

  .text-content {
    color: var(--main-text-color);
    margin-bottom: 5px;
    font-size: 0.94em;
    word-wrap: break-word;
  }

  .read-article {
    margin-left: calc(10px * var(--margin-factor));
    color: var(--primary-text-color);
    font-weight: bold;
    cursor: pointer;
    transition: .15s;
    display: flex;
    align-items: center;
  }

  .read-article:hover {
    transition: .15s;
    color: var(--primary-text-color-highlight);
  }

  .read-article-label {
    margin-right: calc(8px * var(--margin-factor));
  }


  .article-details {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  .article-details.mobile {
    justify-content: center;
  }


  .creation-details {
    font-size: 0.8em;
    color: var(--details-text-color);
    margin-right: calc(30px * var(--margin-factor));
    text-overflow: ellipsis;
    white-space: nowrap;
  }


  .separator {
    width: calc(100% - 40px);
    margin-left: auto;
    margin-right: auto;

    background: var(--primary-separator-color);
    height: 1px;

    margin-top: 15px;
    margin-bottom: 8px;
  }

</style>

<style>
  .article-sum-img {
    width: 100%;
    max-width: 100%;
    height: 100%;
    vertical-align: bottom;
    object-fit: cover;

    border-radius: 30px;
  }
</style>


<script lang="ts">
  import { Article } from '@/interfaces/arcticles/Article.entity';
  import { AuthenticationService } from '@/services/authentication.service';
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import CustomImage from '../CustomImage.component.vue';
  import ArticleRubricList from './ArticleRubricList.component.vue';
  import {Utils} from '@/scripts/Utils';

  const maxDisplayedChars = 240;

  /**
   * @desc affichage en résumé d'un article
   */
  @Component({
    components: {
      'c-img' : CustomImage,
      'article-rubric-list': ArticleRubricList,
    },
  })
  export default class ArticleSum extends Vue {
    /**
     * @desc article à résumer
     */
    @Prop({default: () => {return new Article();}})
    article? : Article;
    
    /**
     * @desc si l'utilisateur est identifié -> s'il peut voir l'article et ses images
     */
    @Prop({default: false})
    identified? : boolean;

    /**
     * @desc retourne un string contenant le nom de l'auteur et la date de création
     */
    private getArticleDetails(authorName : string, createdAt : Date) : string {
      if(!authorName.length) authorName = 'inconnu';
      if(!createdAt) createdAt = new Date();

      let date = createdAt.getDate().toString();
      if(date.length === 1) date = '0' + date;
      let month = (createdAt.getMonth()+1).toString();
      if(month.length === 1) month = '0' + month;
      const strDate = date + '/' + month + "/" + createdAt.getFullYear();

      let outputTxt = 'Rédigé par ' + authorName;
      outputTxt += " le " + strDate;

      return outputTxt;
    }

    /**
     * @desc dermande de lecture de l'article
     * envoie soit vers la page de détails de l'article, soit vers la page de connexion
     */
    private readArticle() {
      if(!this.article) return;
      this.$router.push('/article/' + this.article.id);
    }
  }
</script>
